import { calculatePercentage } from '../atoms/ArticlePremiumContentPercentage';

import { VideoPlayer } from './ArticleContentVideo';
import ArticleGallery from './ArticleGallery';
import ArticlePaidBlockade from './ArticlePaidBlockade';

import { AnalyticsParams } from '@components/Analytics/Google/GAScript';
import { ReplacerPayload } from '@utils/article/processor';
import { Gallery } from '@utils/article/processor/replacer';
import { getPremiumStatus } from '@utils/getters';
import { ArticleViewType, ProcessedArticleI } from 'types/article';

interface ArticleRendererProps {
  className?: string;
  legacyGallery?: string[];
  content?: ReplacerPayload;
}

interface ArticleContentProps {
  article: ProcessedArticleI;
  viewType: ArticleViewType;
  analyticsData: AnalyticsParams;
}

function GalleryRenderer({ meta, legacyGallery }) {
  const galleries: Gallery[] = meta.galleries || [];

  if (legacyGallery) {
    galleries.push({
      images: legacyGallery.map((src) => ({
        src,
        caption: '',
        alt: '',
        width: 1200,
        height: 800,
      })),
    });
  }

  return <>{meta.galleries?.map((gallery: Gallery, key: number) => <ArticleGallery key={key} gallery={gallery} />)}</>;
}

export function ArticleRenderer({ className, legacyGallery, content }: ArticleRendererProps) {
  if (!content) return null;
  const { meta, content: processedContent } = content;

  return (
    <div className={className}>
      <div dangerouslySetInnerHTML={{ __html: processedContent }} />
      <GalleryRenderer meta={meta} legacyGallery={legacyGallery} />
    </div>
  );
}

export default function ArticleContent({ article, viewType, analyticsData }: ArticleContentProps) {
  const isPremiumRequired = getPremiumStatus(article);
  const isBlockadeNecessary = !!article.processedPayContent || (isPremiumRequired && viewType === 'video');
  const isPaidNews = isBlockadeNecessary && !isPremiumRequired;
  const percentageLeft = calculatePercentage(article);
  return (
    <>
      {viewType === 'video' && (
        <VideoPlayer
          article={article}
          isPremiumRequired={isPremiumRequired}
          isLoggedInRequired={isPaidNews}
          isNewsletterRequired={isPaidNews}
        />
      )}
      <article>
        <ArticleRenderer
          className="article-content protected"
          content={article.processedContent}
          legacyGallery={!article.processedPayContent && article.gallery ? article.gallery : undefined}
        />
        <ArticlePaidBlockade
          article={article}
          viewType={viewType}
          alternativeContent={
            article.processedPayContent &&
            article.processedPayContent.content
              ?.replace(/(<([^>]+)>)/gi, '')
              .substr(0, 400)
              .concat('...')
          }
          percentageLeft={percentageLeft}
          isBlockadeNecessary={isBlockadeNecessary}
          premiumRequired={isPremiumRequired}
          loggedInRequired={isPaidNews}
          newsletterRequired={isPaidNews}
          analyticsData={analyticsData}
        >
          <ArticleRenderer
            className="article-content"
            content={article.processedPayContent}
            legacyGallery={article.gallery ? article.gallery : undefined}
          />
        </ArticlePaidBlockade>
      </article>
    </>
  );
}
